// ============================================================
// Datei: src/pages/ressorts/programm/Programm.jsx
//
// Zweck:
// - Hauptseite für das Ressort Programm
// - entscheidet zwischen Desktop- und Mobile-Ansicht
// - lädt Libellen, Schichten und Gäste
//
// ÄNDERUNGEN:
// - loadGuests robuster gemacht
// - bestehende Struktur beibehalten
// ============================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "../../../styles/global.css";
import "../../../styles/ressortTabs.css";
import "../../../styles/ccMailCard.css";

import useVolunteers from "../../../components/fetch/useVolunteers";
import useShifts from "../../../components/fetch/useShifts";
import Api from "../../../Api";

import ProgrammDesktop from "./ProgrammDesktop";
import ProgrammMobile from "./ProgrammMobile";
import useMediaQuery from "../../../hooks/useMediaQuery";

const Programm = () => {

	const isMobile = useMediaQuery("(max-width: 768px)");
	const navigate = useNavigate();

	// ============================================================
	// Volunteers laden
	// ============================================================
	const volunteersHook = useVolunteers("Programm");

	// Rückwärtskompatibel:
	// Falls useVolunteers nur [volunteers, setVolunteers] liefert.
	const volunteers = volunteersHook[0];
	const setVolunteers = volunteersHook[1];
	const reloadVolunteers = volunteersHook[2];

	// ============================================================
	// Schichten für Programm laden
	// ============================================================
	const [shifts] = useShifts("Programm");

	// ============================================================
	// Lokaler State
	// ============================================================
	const [selectedVolunteer, setSelectedVolunteer] = useState(null);
	const [guests, setGuests] = useState([]);
	const [activeTab, setActiveTab] = useState("overview");

	// ============================================================
	// Security
	// Nur OK-Mitglieder aus Programm dürfen diese Seite sehen.
	// ============================================================
	useEffect(() => {
		const role = localStorage.getItem("role");
		const dept = localStorage.getItem("department");

		if (role !== "OK" || dept?.toLowerCase() !== "programm") {
			alert("Zugriff verweigert!");
			navigate("/home");
			return;
		}

		loadGuests();
	}, [navigate]);

	// ============================================================
	// Gästeliste laden
	// ============================================================
	const loadGuests = async () => {
		try {
			const res = await Api.get("/guests");
			setGuests(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error("❌ Programm: Gäste konnten nicht geladen werden:", err);
			setGuests([]);
		}
	};

	// ============================================================
	// CC-Mail-Liste herunterladen
	// ============================================================
	const downloadCcEmails = async () => {
		try {
			const res = await Api.get("/cc-emails", {
				responseType: "blob"
			});

			if (!res.data || res.data.size === 0) {
				throw new Error("Leere Datei");
			}

			const blob = new Blob([res.data], { type: "text/plain" });
			const url = window.URL.createObjectURL(blob);

			const a = document.createElement("a");
			a.href = url;
			a.download = "helferfest_cc_liste.txt";
			document.body.appendChild(a);
			a.click();
			a.remove();

			window.URL.revokeObjectURL(url);

		} catch (err) {
			console.error("❌ CC-Download Fehler:", err);
			alert("Download fehlgeschlagen");
		}
	};

	// ============================================================
	// Gemeinsame Props für Desktop & Mobile
	// ============================================================
	const layoutProps = {
		volunteers,
		setVolunteers,
		reloadVolunteers,
		shifts,
		guests,
		selectedVolunteer,
		setSelectedVolunteer,
		activeTab,
		setActiveTab,
		reloadGuests: loadGuests,
		downloadCcEmails
	};

	return (
		<div
			className="programm-page bg-reveal"
			data-bg={`${process.env.PUBLIC_URL}/backgrounds/background_programm.jpg`}
		>
			<h2 className="programm-heading">Programm</h2>

			{isMobile ? (
				<ProgrammMobile {...layoutProps} />
			) : (
				<ProgrammDesktop {...layoutProps} />
			)}
		</div>
	);
};

export default Programm;